/**
 * 30-10-2026 (Jordy, opdracht C): elke regel in de Live Feed is precies één soort.
 *
 * - Fan      inkomend bericht van de fan                      (type fan_message)
 * - Chatter  verstuurd door een medewerker via het dashboard  (human_message met
 *            sender_identity chatter of admin, geen Mass DM, geen systeemaccount)
 * - Auto     Mass DM (dashboard of platform), auto-MM / promo-MM, welkomstbericht,
 *            AI-antwoord, en alles van een systeemaccount
 * - Other    wat de gegevens niet zeker indelen, bv. een uitgaand bericht dat op het
 *            platform zelf werd verstuurd (niet via het dashboard) en geen
 *            welkomstbericht is. Geen gok.
 *
 * De badges die bijna overal staan ("Shared inbox") en wie een Mass DM startte,
 * gaan naar de info achter het i-knopje van de regel; er gaat niets verloren.
 */

export type FeedSoort = 'fan' | 'chatter' | 'auto' | 'other'

export interface FeedRegelBron {
  type: string
  platform?: string | null
  chatter?: string | null
  sender_admin_name?: string | null
  sender_identity?: string | null
  mass_message_log_id?: number | null
  mass_message_kind?: string | null
  /** Backend: "welcome" voor het automatische welkomstbericht. */
  auto_kind?: string | null
  inbox_kind?: string | null
}

export interface FeedIndeling {
  soort: FeedSoort
  /** Het woord op de badge: Fan / Chatter / Auto / Other. */
  woord: string
  /** Achter het woord: de chatternaam, of het type automatisch bericht. */
  detail: string | null
  /** Regels voor de info achter het i-knopje (niet in de rij zelf). */
  info: string[]
}

/** De accounts waaronder het systeem zelf Mass Messages maakt (mm_auto, mm_promo). */
const SYSTEEM: Record<string, string> = {
  'mm-auto-systeem': 'Auto MM',
  'mm-promo-systeem': 'Promo MM',
}

export const SOORT_ICOON: Record<FeedSoort, string> = {
  fan: '↘', chatter: '↗', auto: '⚙', other: '?',
}

/** Bakken die als badge in de rij blijven: ze zeggen waarom een regel aandacht vraagt. */
export const BAK_LABEL: Record<string, string> = {
  private: 'Private chat', timewaster: 'Timewaster', spender: 'Spender',
}

export function feedIndeling(r: FeedRegelBron): FeedIndeling {
  const naam = String(r.sender_admin_name ?? r.chatter ?? '').trim()
  const systeem = SYSTEEM[naam.toLowerCase()]
  const fc = String(r.platform ?? '').toLowerCase() !== 'f2f'
  const info: string[] = []
  if (String(r.inbox_kind ?? '').toLowerCase() === 'shared') info.push('Shared inbox')

  if (r.type === 'fan_message') {
    return { soort: 'fan', woord: 'Fan', detail: null, info }
  }
  if (r.type === 'ai_response') {
    info.unshift('AI reply')
    return { soort: 'auto', woord: 'Auto', detail: 'AI', info }
  }
  if (r.type === 'mass_message' || r.mass_message_log_id) {
    const platformEigen = String(r.mass_message_kind ?? '').toLowerCase().includes('native')
    const bron = platformEigen ? 'Platform Mass DM' : 'Dashboard Mass DM'
    info.unshift(systeem ? `${bron} by the system (${naam})` : `${bron} · started by ${naam || 'unknown'}`)
    return { soort: 'auto', woord: 'Auto', detail: systeem ?? 'Mass DM', info }
  }
  if (String(r.auto_kind ?? '').toLowerCase() === 'welcome') {
    info.unshift(`Welcome message, sent automatically on ${fc ? 'FanCentro' : 'F2F'}`)
    return { soort: 'auto', woord: 'Auto', detail: 'Welcome', info }
  }
  if (r.type === 'human_message' && systeem) {
    info.unshift(`Sent by the system (${naam})`)
    return { soort: 'auto', woord: 'Auto', detail: systeem, info }
  }
  if (r.type === 'human_message' && (r.sender_identity === 'chatter' || r.sender_identity === 'admin')) {
    info.unshift('Sent from the dashboard')
    return { soort: 'chatter', woord: 'Chatter', detail: naam || 'unknown', info }
  }
  if (r.type === 'human_message' && r.sender_identity === 'platform_direct') {
    info.unshift(`Sent on ${fc ? 'FanCentro' : 'F2F'} directly, not via the dashboard`)
  } else {
    info.unshift(`Unknown source (${String(r.type || 'no type')})`)
  }
  return { soort: 'other', woord: 'Other', detail: null, info }
}
